import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../app/CartContext';
import { useLogoutMutation } from '../services/auth/api';
import { useGetRegionsQuery } from '../services/category/api';
import { ConstantUtil } from '../utils/ConstantUtil';
import { UserIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import logoWhite from '../assets/images/logo_uuspa.png';
import logo from '../assets/images/logo.png';
import downArrow from '../assets/images/icon_down-arrow-primary.png';
import iconShoppingCart from '../assets/images/icon_shopping-cart.svg';
import iconShoppingCartWhite from '../assets/images/icon_shopping-cart-white.png';



interface NavItem {
  name: string;
  href: string;
}

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { state: cartState, toggleCart, clearCartCompletely } = useCart();
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const [navigation, setNavigation] = useState<NavItem[]>([
    { name: '全部商品', href: '/courses' }
  ]);
  const [countTimer, setCountTimer] = useState('00:00:00');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTransparent, setIsTransparent] = useState(false);
  const [isMobileRegionsOpen, setIsMobileRegionsOpen] = useState(false);

  // API 地區資料
  const { data } = useGetRegionsQuery({ includeDistricts: true });
  const regions = data?.data?.regions || {};

  // 把 cities 物件轉成陣列
  const getCities = (regionKey: string) => {
    const citiesObj = regions[regionKey]?.cities || {};
    return Object.entries(citiesObj).map(([cityKey, city]: [string, any]) => ({
      key: cityKey,
      ...city,
    }));
  };

  // dropdown timer
  const dropdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    console.log(location)
    if(location.pathname === '/courses' || location.pathname === '/'){
      setIsTransparent(false)
    } else{
      setIsTransparent(true)
    }
  }, [location]);
  
  // 鎖住外層滾動
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // 清理，避免殘留
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);
  // 初始化分類 (其它導航)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fakeApiResult = [
          { name: '肌膚保養1', href: '/category/skincare' },
          { name: '指壓按摩2', href: '/category/massage' },
          // { name: '熱銷TOP', href: '/category/hotsale' },
          // { name: '最新上市', href: '/promotion/new' },
          // { name: '廣告行銷活動1', href: '/service/new-opening' },
          // { name: '共用的頁面', href: '/page/about-us' },
        ];
        setNavigation([
          { name: '全部商品', href: '/courses' },
          ...fakeApiResult
        ]);
      } catch (error) {
        console.error("載入分類失敗", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
  // 初始檢查一次
  const checkLoginStatus = () => {
    const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN);
    const refreshToken = localStorage.getItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN);
    const storedUserInfo = localStorage.getItem(ConstantUtil.STORAGE_USER_INFO);
    setIsLoggedIn(!!(token && refreshToken && storedUserInfo));
  };
  checkLoginStatus();

  // 監聽自訂事件（你在登入成功時 dispatch 的）
  const handleLocalStorageChange = (event: Event) => {
    const customEvent = event as CustomEvent<{ key: string; action: string }>;
    if (customEvent.detail.key === ConstantUtil.STORAGE_USER_TOKEN) {
      checkLoginStatus(); // 重新檢查登入狀態
    }
  };

  window.addEventListener('localStorageChange', handleLocalStorageChange);

  // ✅ 跨分頁同步登入登出
  const handleNativeStorageEvent = (event: StorageEvent) => {
    if (event.key === ConstantUtil.STORAGE_USER_TOKEN) {
      checkLoginStatus();
    }
  };
  window.addEventListener('storage', handleNativeStorageEvent);

  // 清除監聽
  return () => {
    window.removeEventListener('localStorageChange', handleLocalStorageChange);
    window.removeEventListener('storage', handleNativeStorageEvent);
  };
}, []);


  // 登出
  const handleLogout = async () => {
  try {
    await logout().unwrap();
  } catch (error) {
    console.error('登出錯誤:', error);
  } finally {
    // ✅ 新增：清空購物車 (localStorage + Context)
    clearCartCompletely(); 
    localStorage.removeItem('cart');

    localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN);
    localStorage.removeItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN);
    localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO);
    setIsLoggedIn(false);
    navigate('/');
  }
};

  // nav active 判斷
  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // dropdown
  const handleDropdownEnter = (region: string) => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setActiveDropdown(region);
  };
  const handleDropdownLeave = () => {
    dropdownTimerRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 800);
  };
  useEffect(() => {
    let timer: NodeJS.Timeout;

    // 將字串轉成總秒數
    const [h, m, s] = countTimer.split(':').map(Number);
    let totalSeconds = h * 3600 + m * 60 + s;

    timer = setInterval(() => {
      totalSeconds -= 1;

      if (totalSeconds <= 0) {
        clearInterval(timer);
        setCountTimer('00:00:00');
        return;
      }

      // 計算新的時、分、秒
      const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');

      // 更新顯示格式
      setCountTimer(`${hours}:${minutes}:${seconds}`);
    }, 1000);

    return () => clearInterval(timer); // ✅ 離開時清除 interval
  }, []);


  return (
    <header className='fixed top-0 left-0 w-full z-50' style={{
              boxShadow: (isTransparent || isScrolled) ? "0px 1px 2px 0px rgba(21, 30, 40, 0.08)" : "",
            }}>
      {/* 頂部促銷橫幅 */}
      {/* <div className="bg-[#47819a] text-white py-2">
        <div className="container mx-auto px-4 text-center md:block flex justify-between items-center flex-col marquee">
          <span className="text-sm font-medium">
            新用戶首次購買享85折優惠！立即註冊領取優惠券
          </span>
          <span className="text-sm font-medium">倒數 {countTimer}</span>
          <span className="text-sm font-medium ml-5">
            全新上架！探索我們的夏季限定SPA體驗，讓您清涼一夏
          </span>
        </div>
      </div> */}

      <div className="lg:px-8 px-5"
        style={{
              background: (isTransparent || (isTransparent || isScrolled)) ? "rgba(255, 255, 255, 0.8)" : "transparent",
            }}>
        <div className="max-w-[1680px] mx-auto">
          {/* 第一行 */}
          <div
            className={`flex justify-between items-center ease-in-out ${
              (isTransparent || isScrolled) ? "transition-all duration-500" : "transition-all duration-200"
            }`}
            style={{
              paddingTop: (isTransparent || isScrolled) ? "0px" : "5px",
              paddingBottom: (isTransparent || isScrolled) ? "0px" : "5px",
            }}
          >
            <Link
                to="/"
                className="text-2xl font-bold text-gray-900 hover:text-[#47819a] transition-colors"
              >
                <img src={(isTransparent || isScrolled)?logo:logoWhite} alt="UUspa Logo" className="h-[30px] lg:h-[50px]" />
              </Link>
           <div className="flex items-center space-x-8">
              {/* 桌機版導覽 */}
            <nav className="hidden lg:flex space-x-8">
              {/* 地區下拉 */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownEnter('regions')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  className={`px-3 py-2 text-subtitle-m rounded-md transition-colors flex items-center space-x-1 ${
                    activeDropdown === 'regions'
                      ? 'text-[#3f8bb0]'
                      : 'text-primary'
                  }`}
                  style={{
                  color: (isTransparent || isScrolled) ? "#5092AF" : "#ffffff",
                }}
                >
                  <span>地區</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {activeDropdown === 'regions' && (
                  <div className="absolute top-full left-0 mt-1 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-6">
                        {Object.entries(regions).map(([key, region]) => (
                          <div key={key} className="space-y-2">
                            <h4 className="font-medium text-primary border-b border-gray-100 pb-1">
                              <Link to={`/${key}`} className="text-primary-dark font-bold hover:text-[#3f8bb0]">
                                {region.name}
                              </Link>
                            </h4>
                            <div className="space-y-1">
                              {getCities(key).map((city: any) => (
                                <Link
                                  key={city.key}
                                  to={`/${key}/${city.key}`}
                                  className="block text-gray-700 text-sm hover:text-[#3f8bb0]"
                                >
                                  {city.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* 全部商品 */}
              <Link
                to="/courses"
                className={`px-3 py-2 text-subtitle-m rounded-md transition-colors ${
                  isActive('/courses')
                    ? 'text-[#3f8bb0]'
                    : 'text-primary hover:text-primary-dark '
                }`}
                 style={{
                  color: (isTransparent || isScrolled) ? "#5092AF" : "#ffffff",
                }}
              >
                全部商品
              </Link>

              

              {/* 其它導航 (分類/活動) */}
              {navigation.slice(1).map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 text-subtitle-m rounded-md transition-colors ${
                    isActive(item.href)
                      ? 'text-[#3f8bb0]'
                      : 'text-primary hover:text-primary-dark '
                  }`}
                  style={{
                  color: (isTransparent || isScrolled) ? "#5092AF" : "#ffffff",
                }}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            </div>
          <div className="flex justify-between items-center py-5 md:py-6">
            {/* 右側購物車 / 登入 */}
            <div className="flex items-center">
              {/* 購物車 */}
              <button
                onClick={toggleCart}
                className="text-gray-500 hover:text-primary-dark transition-colors relative mr-5 md:mr-8"
              >
                {(isTransparent || isScrolled) ? (
                  <img src={iconShoppingCart} className='w-[24px] md:w-[42px]' />
                ) : (
                  <img src={iconShoppingCartWhite} className='w-[24px] md:w-[42px]' />
                )}
                {cartState.items.length > 0 && (
                  <span className="absolute -top-2 -right-2 md:-top-1 md:-right-1 bg-warning text-white text-body-xxs rounded-full md:w-5 md:h-5 h-4 w-4 flex items-center justify-center">
                    {cartState.items.length}+
                  </span>
                )}
              </button>

              {/* 登入 / 登出 */}
              {isLoggedIn ? (
                <div className="flex items-center hidden lg:block">
                  <button
                    onClick={() => navigate('/profile')}
                    className="hidden md:flex items-center space-x-2 text-gray-500 hover:text-primary-dark transition-colors"
                  >
                    <UserIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">會員中心</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="hidden md:flex items-center space-x-2 text-gray-500 hover:text-red-600 transition-colors"
                  >
                    {isLoggingOut ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    ) : (
                      <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    )}
                    <span className="text-sm font-medium">登出</span>
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden lg:flex items-center text-[#EEF4F7] rounded-full px-6 py-2 bg-primary transition-colors"
                  style={{
                    backgroundColor: (isTransparent || isScrolled) ? '#3f8bb0' : '#EEF4F7',
                    color: (isTransparent || isScrolled) ? '#ffffff' : '#101D23',
                  }}
                >
                  {/* <UserIcon className="w-5 h-5" /> */}
                  <span className="text-sm font-medium">登入</span>
                </Link>
              )}
            </div>

            {/* 手機版按鈕 */}
            <div className="lg:hidden flex items-center justify-center">
              <button
                onClick={() => {setIsMenuOpen(!isMenuOpen);setIsMobileRegionsOpen(false);}}
                className=" hover:text-primary-dark"
                style={{
                  color: (isTransparent || isScrolled) ? "#5092AF" : "#ffffff",
                }}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
              </button>
            </div>
          </div>
          </div>
          {/* 手機版選單 */}
          
        </div>
      </div>
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 w-full h-screen overflow-y-auto bg-white z-[60]">
          <div className="p-5 flex justify-end">
            <button onClick={() => setIsMenuOpen(false)} type="button">
              <svg className="h-6 w-6" fill="none" stroke="#5092AF" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className='flex flex-col justify-between items-center h-[calc(100vh-64px)]'>
            <div className="w-full p-6">
              {/* 地區 */}
              <div className="space-y-1">
                <div className="py-5 flex items-center justify-between text-subtitle-m text-primary rounded-md" onClick={() => setIsMobileRegionsOpen(!isMobileRegionsOpen)}>
                  地區
                  <img src={downArrow} className="h-[22px] ml-2" style={{ transform: isMobileRegionsOpen ? 'none' : 'rotate(180deg)' }} />
                </div>
                {isMobileRegionsOpen && Object.entries(regions).map(([key, region]) => (
                  <div key={key} className="space-y-1 text-primary-dark">
                    <Link
                      to={`/${key}`}
                      className={`block text-body-s py-3 font-bold rounded-md transition-colors ${
                        isActive(`/${key}`)
                          ? ''
                          : 'hover:text-primary-dark'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {region.name}
                    </Link>
                    <div className="space-y-1">
                      {getCities(key).map((city: any) => (
                        <Link
                          key={city.key}
                          to={`/${key}/${city.key}`}
                          className="py-3 px-4 block text-body-s rounded-md transition-colors text-gray-700 hover:text-[#47819a]"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {city.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {/* 全部商品 */}
              <Link
                to="/courses"
                className={`block py-5 text-subtitle-m rounded-md transition-colors ${
                  isActive('/courses')
                    ? 'text-[#47819a]'
                    : 'text-primary hover:text-primary-dark'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                全部商品
              </Link>
              {/* 其它導航 (分類/活動) */}
              {navigation.slice(1).map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block py-5 text-subtitle-m rounded-md transition-colors ${
                    isActive(item.href)
                      ? 'text-[#47819a]'
                      : 'text-primary hover:text-primary-dark'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            <div className="w-full border-t border-gray-200 pt-5 px-8 pb-8">
              {/* 登入 / 登出 */}
              <div className='flex justify-between items-center'>
                <button onClick={toggleCart} className="relative mr-2 w-[42px] shrink-0">
                  <img src={iconShoppingCart} alt="購物車" className="block w-full" />
                  {cartState.items.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-warning text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartState.items.length}
                    </span>
                  )}
                </button>
                {isLoggedIn ? (
                  <div className="w-full flex justify-between items-center">
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-base font-medium rounded-md transition-colors text-gray-500 hover:text-primary-dark hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-2">
                        <UserIcon className="w-5 h-5" />
                        <span>會員中心</span>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      disabled={isLoggingOut}
                      className="text-center py-4 bg-primary rounded-full text-white w-full"
                    >
                        <span>登出</span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="text-button-s text-center py-4 bg-primary rounded-full text-white w-full"
                    onClick={() => setIsMenuOpen(false)}
                  >
                      <span>登入</span>
                  </Link>
                )}
              </div>
              
            </div>
          </div>
          
        </div>
      )}
    </header>
  );
};

export default Header;
